import { ReactElement, SVGAttributes, useEffect, useState } from 'react'
import { hasFlag } from 'country-flag-icons'
import { Globe } from '../../icons'

import { cn } from '../../lib/utils'

type FlagComponent = (props: SVGAttributes<SVGElement> & { title?: string }) => ReactElement

type FlagSet = Record<string, FlagComponent | undefined>

/**
 * The SVG set is ~250 flags: it loads once, on the first flag drawn, as its
 * own chunk, so pages without flags never pay for it.
 */
let loadedFlags: FlagSet | null = null
let flagsRequest: Promise<FlagSet> | null = null

function loadFlags(): Promise<FlagSet> {
  if (!flagsRequest) {
    flagsRequest = import('country-flag-icons/react/3x2')
      .then((module) => {
        loadedFlags = module as unknown as FlagSet
        return loadedFlags
      })
      .catch((error: unknown) => {
        flagsRequest = null
        throw error
      })
  }
  return flagsRequest
}

/**
 * An ISO 3166-1 alpha-2 code ("mx"), or a subdivision code ("GB-ENG"), to the
 * code the flag set draws; a subdivision without its own flag falls back to
 * its country. Null for anything without a flag ("Global", a region, "").
 */
export function resolveFlagCode(code: string | null | undefined): string | null {
  const raw = code?.trim().toUpperCase().replace(/_/g, '-')
  if (!raw) {
    return null
  }
  if (hasFlag(raw)) {
    return raw
  }
  const head = raw.split('-')[0]
  return head !== raw && hasFlag(head) ? head : null
}

export interface CountryFlagProps {
  /** ISO alpha-2 or subdivision code. Missing or unknown draws a globe. */
  code?: string | null
  /** Accessible name. Without it the flag is decorative (the label beside it names the market). */
  title?: string
  className?: string
  'data-testid'?: string
}

export function CountryFlag(props: CountryFlagProps) {
  const { code, title, className, 'data-testid': testId } = props

  const flagCode = resolveFlagCode(code)
  const [flags, setFlags] = useState<FlagSet | null>(loadedFlags)

  useEffect(() => {
    if (!flagCode || flags) {
      return
    }
    let isCancelled = false
    loadFlags()
      .then((set) => {
        if (!isCancelled) {
          setFlags(set)
        }
      })
      .catch(() => {
        // The placeholder stays: a flag is decoration, never worth an error.
      })
    return () => {
      isCancelled = true
    }
  }, [flagCode, flags])

  const boxClassName = cn('inline-block h-3 w-[18px] shrink-0 overflow-hidden rounded-[2px]', className)
  const a11y = title
    ? { role: 'img' as const, 'aria-label': title }
    : { 'aria-hidden': true as const }

  if (!flagCode) {
    return (
      <Globe
        className={cn('h-3.5 w-3.5 shrink-0', className)}
        strokeWidth={1.75}
        data-testid={testId}
        data-flag="global"
        {...a11y}
      />
    )
  }

  const Flag = flags?.[flagCode.replace(/-/g, '_')]

  if (!Flag) {
    return (
      <span
        className={cn(boxClassName, 'bg-muted')}
        data-testid={testId}
        data-flag={flagCode}
        data-flag-state="loading"
        {...a11y}
      />
    )
  }

  return (
    <span
      className={cn(boxClassName, 'shadow-[0_0_0_1px_hsl(var(--border))]')}
      data-testid={testId}
      data-flag={flagCode}
      data-flag-state="ready"
      {...a11y}
    >
      <Flag className="block h-full w-full" preserveAspectRatio="xMidYMid slice" />
    </span>
  )
}
